import Link from 'next/link';
import getConfig from 'next/config';
import styled from 'styled-components';
import { RefObject, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';

import { EmailNewsletter } from 'layout/footer/components/EmailNewsletter';
import { FeatureFlags } from 'featureFlags/constants';
import { FooterNavigationDesktop } from 'layout/footer/components/FooterNavigationDesktop';
import { FooterNavigationMobile } from 'layout/footer/components/FooterNavigationMobile';
import { UnorderedList } from 'shared/components/UnorderedList';
import { variableFontRegular } from 'styles/theme/text';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { useWindowSize } from 'shared/hooks/useWindowSize';
import { OneTrustLink } from 'tracking/components/OneTrustLink';
import { useSitePreferencesModal } from 'shared/hooks/useSitePreferencesModal';
import { t } from 'localization/utils/t';

const TP = 'layout.footer.components.Footer';

interface IFooterProps {
  footerRef?: RefObject<HTMLElement>;
  setIsBuyBarVisible?: (val: boolean) => void;
}

const Footer = ({ footerRef, setIsBuyBarVisible }: IFooterProps) => {
  const { isMobile } = useWindowSize();
  const { publicRuntimeConfig } = getConfig();
  const isProd = publicRuntimeConfig.RELEASE_ENV === 'production';
  const isOneTrustEnabled = useFeatureFlag(FeatureFlags.WEB_ENABLE_ONE_TRUST);
  const isGiftCardCheckBalanceEnabled = useFeatureFlag(
    FeatureFlags.TEMP_WEB_ENABLE_GIFT_CARD_CHECK_BALANCE,
  );
  const careersLink = 'https://boards.greenhouse.io/goatgroup';

  const { openSitePreferencesModal } = useSitePreferencesModal();

  /*
   * To keep snapshots consistent, adding this state so that
   * if cypress is running we don't display the prenv in the footer to keep
   * snapshots consistent
   */
  const [isCypress, setIsCypress] = useState(false);

  useEffect(() => {
    // @ts-ignore cypress sets this window value
    if (window?.Cypress) {
      setIsCypress(true);
    }
  }, []);

  const footerData = [
    {
      id: 1,
      title: t(`${TP}.about`, 'About'),
      breadcrumbs: [
        {
          id: 1,
          name: t(`${TP}.careers`, 'Careers'),
          externalUrl: careersLink,
          openInNewTab: true,
        },
        { id: 2, name: t(`${TP}.goatApp`, 'GOAT App'), slug: 'app', openInNewTab: false },
      ],
    },
    {
      id: 2,
      title: t(`${TP}.buy`, 'Buy'),
      breadcrumbs: [
        {
          id: 1,
          name: t(`${TP}.assuranceOfAuthenticity`, 'Assurance of Authenticity'),
          slug: 'verification',
          openInNewTab: true,
        },
        {
          id: 2,
          name: t(`${TP}.buyerProtectionPolicy`, 'Buyer Protection Policy'),
          slug: 'buyer-protection-guarantee',
          openInNewTab: true,
        },
        {
          id: 3,
          name: t(`${TP}.purchasesAndReturns`, 'Purchases & Returns'),
          slug: 'returns',
          openInNewTab: true,
        },
        {
          id: 4,
          name: t(`${TP}.goatClean`, 'GOAT Clean'),
          slug: 'goat-clean',
          openInNewTab: false,
        },
        {
          id: 5,
          name: t(`${TP}.giftCards`, 'Gift Cards'),
          slug: 'gift-cards',
          openInNewTab: false,
        },
      ],
    },
    {
      id: 3,
      title: t(`${TP}.sell`, 'Sell'),
      breadcrumbs: [
        {
          id: 1,
          name: t(`${TP}.sellingOnGoat`, 'Selling on GOAT'),
          slug: 'selling',
          openInNewTab: true,
        },
        {
          id: 2,
          name: t(`${TP}.feePolicy`, 'Fee Policy'),
          slug: 'fees',
          openInNewTab: true,
        },
        {
          id: 3,
          name: t(`${TP}.dropOff`, 'Drop Off'),
          slug: 'goat-group-dropoff-policy',
          openInNewTab: true,
        },
      ],
    },
    {
      id: 4,
      title: t(`${TP}.support`, 'Support'),
      breadcrumbs: [
        {
          id: 1,
          name: t(`${TP}.goatSupport`, 'GOAT Support'),
          openInNewTab: true,
          externalUrl: 'https://support.goat.com/hc/en-us',
        },
        {
          id: 2,
          name: t(`${TP}.checkGiftCardBalance`, 'Check Gift Card Balance'),
          slug: 'gift-cards/balance',
          openInNewTab: true,
        },
      ],
    },
  ];

  const enabledFooterData = isGiftCardCheckBalanceEnabled ? footerData : footerData.slice(0, -1);

  return (
    <Footer.Wrapper id="site-footer" ref={footerRef}>
      <Footer.FlexWrapper>
        <Footer.NewsLetterWrapper>
          <EmailNewsletter setIsBuyBarVisible={setIsBuyBarVisible} />
        </Footer.NewsLetterWrapper>

        <Footer.LinksWrapper>
          {isMobile ? (
            <FooterNavigationMobile footerLinks={enabledFooterData} />
          ) : (
            <FooterNavigationDesktop footerLinks={enabledFooterData} />
          )}
          <Footer.StaticLinks>
            {!isGiftCardCheckBalanceEnabled && (
              <Footer.NavLink data-qa="footer_support">
                <Link href="https://support.goat.com" passHref prefetch={false}>
                  <a target="_blank" rel="noreferrer noopener">
                    {t(`${TP}.support`, 'Support')}
                  </a>
                </Link>
              </Footer.NavLink>
            )}
            <Footer.NavLink data-qa="footer_terms">
              <Link href="/terms" passHref prefetch={false}>
                <a target="_blank" rel="noreferrer noopener">
                  {t(`${TP}.terms`, 'Terms')}
                </a>
              </Link>
            </Footer.NavLink>
            <Footer.NavLink data-qa="footer_privacy">
              <Link href="/privacy" passHref prefetch={false}>
                <a target="_blank" rel="noreferrer noopener">
                  {t(`${TP}.privacy`, 'Privacy')}
                </a>
              </Link>
            </Footer.NavLink>
            {isOneTrustEnabled && (
              <Footer.NavLink data-qa="footer_cookies">
                <OneTrustLink />
              </Footer.NavLink>
            )}
            <Footer.NavLink data-qa="footer_site_preferences">
              <button type="button" onClick={openSitePreferencesModal}>
                {t(`${TP}.locationPreferences`, 'Location Preferences')}
              </button>
            </Footer.NavLink>
            <Footer.NavLink data-qa="footer_sitemap">
              <Link href="/sitemap-html" passHref prefetch={false}>
                <a>{t(`${TP}.sitemap`, 'Sitemap')}</a>
              </Link>
            </Footer.NavLink>
          </Footer.StaticLinks>
          {/* [Non-production] Display current RELEASE_ENV */}
          {!isProd && !isCypress && (
            <Footer.DevEnvironment>
              <span>[{publicRuntimeConfig.RELEASE_ENV}]</span>
            </Footer.DevEnvironment>
          )}
        </Footer.LinksWrapper>

        <Footer.MobileAppDownload>
          <Footer.DownloadAppText>
            {t(
              `${TP}.downloadAppText`,
              'Never miss a drop again. Download the GOAT app for reminders and exclusive promotions.',
            )}
          </Footer.DownloadAppText>
          <Footer.DownloadAppButton
            href="https://goat.app.link/C8eM8PorVbb"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t(`${TP}.downloadApp`, 'Download App')}
          </Footer.DownloadAppButton>
        </Footer.MobileAppDownload>
      </Footer.FlexWrapper>

      <Footer.Copyright>
        <Trans
          i18nKey={`${TP}.copyright`}
          defaults="<0>© {year}</0><0> 1661, Inc. All Rights Reserved</0>"
          components={[<span />]}
          values={{ year: new Date().getFullYear() }}
        />
      </Footer.Copyright>
    </Footer.Wrapper>
  );
};

Footer.Wrapper = styled.footer`
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: ${({ theme }) => theme.navigation.backgroundColor};
  padding-top: 64px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    justify-content: space-between;
    align-items: flex-start;
    padding: 64px 32px 24px;
  }
`;

Footer.FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    flex-direction: row;
    min-height: 81px;
  }
`;

Footer.NewsLetterWrapper = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    order: 2;
  }
`;

Footer.LinksWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    flex-direction: row;
    height: fit-content;
    padding-top: 4px;
  }
`;

Footer.StaticLinks = styled(UnorderedList)`
  display: flex;
  flex-wrap: wrap;
  padding: 9px 20px 0;
  row-gap: 18px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding: 0;
  }
`;

Footer.NavLink = styled.li`
  margin-right: 16px;
  cursor: pointer;

  > button {
    padding: 0;

    :hover {
      text-decoration: underline;
    }
  }

  :hover {
    text-decoration: underline;
  }
`;

Footer.MobileAppDownload = styled.div`
  color: ${({ theme }) => theme.navigation.downloadAppText};
  margin: 40px 20px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    display: none;
  }
`;

Footer.DownloadAppText = styled.div`
  margin-bottom: 24px;
  line-height: 16px;
`;

Footer.DownloadAppButton = styled.a`
  color: ${({ theme }) => theme.navigation.textColor};
  background-color: ${({ theme }) => theme.navigation.downloadAppBackground};
  border: 1px solid ${({ theme }) => theme.navigation.downloadAppBorder};
  padding: 10px;
  text-transform: uppercase;
  ${variableFontRegular({ size: '9.5px', height: '12px' })};
`;

Footer.DevEnvironment = styled.div`
  padding: 15px 20px 0;

  span {
    color: ${({ theme }) => theme.colors.darkGray};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    padding: 0;
  }
`;

Footer.Copyright = styled.div`
  margin: 20px;

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin: 0;
  }
`;

export default Footer;
